import { RightOutlined } from '@ant-design/icons'
import { Collapse } from 'antd'
import { useEffect, useMemo, useState } from 'react'

import type { CharacteristicFilterType } from '@/types'
import {
  countSelectedSlots,
  formatSlottedValues,
  formatSlotValue,
  getSlotLabel,
  groupSlotOptions,
  isUniformSlottedEligible,
} from '@/utils/search/characteristics/slottedCharacteristics'

import { toggleCharacteristicOptionValue } from '../Filters.utils'
import generalCn from '../GeneralFiltersStyles.module.scss'
import HighlightText from '../HighlightText/HighlightText'
import UniformSlottedCheckbox from '../UniformSlottedCheckbox/UniformSlottedCheckbox'
import cn from './FilterSlotted.module.scss'

const EMPTY_VALUES: string[] = []

type FilterSlottedPropsType = {
  item: CharacteristicFilterType
  selectedItems: CharacteristicFilterType[]
  title: string
  collapseItemKey: string
  setSelectedItems: (selectedItems: CharacteristicFilterType[]) => void
  forceOpen?: boolean
  searchQuery?: string
  matchUniform: boolean
  onMatchUniformChange: (checked: boolean) => void
  pinnedValues?: string[]
}

/**
 * Фильтр слотовой характеристики («Диаметр присоединения»):
 * по одному ряду кнопок быстрого выбора на каждое присоединение.
 * Внутри присоединения — множественный выбор (ИЛИ), между присоединениями — И.
 */
function FilterSlotted(props: FilterSlottedPropsType) {
  const {
    item,
    selectedItems,
    title,
    collapseItemKey,
    setSelectedItems,
    forceOpen,
    searchQuery = '',
    matchUniform,
    onMatchUniformChange,
    pinnedValues = EMPTY_VALUES,
  } = props

  const isApplied = selectedItems.some((sel) => sel.code === item.code)
  const [isOpen, setIsOpen] = useState(isApplied)

  useEffect(() => {
    if (!isApplied) setIsOpen(false)
  }, [isApplied])

  const selectedValues = useMemo(() => {
    const selectedChar = selectedItems.find((sel) => sel.code === item.code)
    if (!selectedChar) return [] as string[]

    return Array.from(
      new Set([
        ...(selectedChar.options?.map((option) => String(option.value)) || []),
        ...(selectedChar.chosenOptions || []),
      ])
    )
  }, [selectedItems, item.code])

  // Бэкенд считает фасет отдельно для каждого присоединения: фильтр самого
  // присоединения из его же корзины исключается (множественный выбор = ИЛИ),
  // а фильтры соседних присоединений применяются (= И). Поэтому значения
  // текущего слота остаются в ответе после выбора, а соседние сужаются —
  // копить ничего не нужно, список берём из свежего ответа как есть.
  const slotGroups = useMemo(
    () =>
      groupSlotOptions(
        item.options,
        selectedValues,
        item.slotsCount,
        pinnedValues
      ),
    [item.options, selectedValues, item.slotsCount, pinnedValues]
  )

  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues])
  const selectedSlotsCount = countSelectedSlots(selectedValues)
  const summary = formatSlottedValues(selectedValues)
  const unit = item.unitOfMeasurementDto?.description
  const isUniformEligible = useMemo(
    () => isUniformSlottedEligible(selectedValues),
    [selectedValues]
  )

  const toggleValue = (rawValue: string) =>
    setSelectedItems(
      toggleCharacteristicOptionValue(selectedItems, item, rawValue)
    )

  return (
    <div className={generalCn.filterContainer}>
      <div className={generalCn.collapseContainer}>
        <Collapse
          activeKey={forceOpen || isOpen ? [collapseItemKey] : []}
          onChange={() => {
            if (!forceOpen) setIsOpen((prev) => !prev)
          }}
          expandIcon={({ isActive }) => (
            <RightOutlined
              className={`${generalCn.expandIcon} ${isActive ? generalCn.expandIconActive : ''}`}
            />
          )}
          items={[
            {
              key: collapseItemKey,
              label: (
                <div className={generalCn.collapseHeaderContainer}>
                  <span className={cn.headerTitle}>
                    <HighlightText text={title} query={searchQuery} />
                    {unit && <span className={cn.unit}>({unit})</span>}
                  </span>
                  {selectedSlotsCount > 0 && (
                    <button
                      onClick={(event) => {
                        event.stopPropagation()
                        setSelectedItems(
                          selectedItems.filter((sel) => sel.code !== item.code)
                        )
                      }}
                    >
                      Сбросить
                    </button>
                  )}
                </div>
              ),
              children: (
                <div className={generalCn.collapseItemContent}>
                  {slotGroups.length === 0 ? (
                    <div className={cn.emptyText}>Нет доступных значений</div>
                  ) : (
                    <>
                      {summary ? (
                        <div className={cn.summary}>
                          Итоговое значение: <strong>{summary}</strong>
                        </div>
                      ) : (
                        <div className={cn.hint}>
                          Выберите диаметры для каждого присоединения
                        </div>
                      )}

                      <div className={cn.uniformRow}>
                        <UniformSlottedCheckbox
                          checked={matchUniform && isUniformEligible}
                          disabled={!isUniformEligible}
                          onChange={onMatchUniformChange}
                        />
                      </div>

                      {slotGroups.map((group) => (
                        <div className={cn.slotGroup} key={group.index}>
                          <div className={cn.slotHeader}>
                            <span className={cn.slotTitle}>
                              {getSlotLabel(group.index)}
                            </span>
                          </div>
                          {group.options.length === 0 && (
                            <span className={cn.slotEmpty}>
                              Нет доступных значений
                            </span>
                          )}
                          <div className={cn.slotPills}>
                            {group.options.map((option) => {
                              const isChecked = selectedSet.has(option.rawValue)

                              return (
                                <button
                                  type="button"
                                  key={option.rawValue}
                                  className={[
                                    cn.slotPill,
                                    isChecked ? cn.slotPillChecked : '',
                                  ]
                                    .filter(Boolean)
                                    .join(' ')}
                                  aria-pressed={isChecked}
                                  onClick={() => toggleValue(option.rawValue)}
                                >
                                  <HighlightText
                                    text={formatSlotValue(option.value)}
                                    query={searchQuery}
                                  />
                                  <span className={cn.slotPillCount}>
                                    {option.count}
                                  </span>
                                </button>
                              )
                            })}
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              ),
            },
          ]}
        />
      </div>
    </div>
  )
}

export default FilterSlotted
